import React from "react";
import useSWR from "swr";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/app/DatePicker";
import { DownloadSimple } from "@phosphor-icons/react";
import { fetcher, fmtN, todayISO, downloadXlsx } from "./common";
import DsfPanel from "./DsfPanel";

export default function TaxDeclarations({ file }) {
  const [from, setFrom] = React.useState(`${new Date().getFullYear()}-01-01`);
  const [to, setTo] = React.useState(todayISO());
  const { data } = useSWR(`/accounting/files/${file.id}/tax/tva?from_d=${from}&to_d=${to}`, fetcher);

  const Section = ({ title, rows, total }) => (
    <div className="border border-zinc-200 bg-white rounded-md overflow-hidden">
      <div className="px-4 py-2 border-b border-zinc-200 text-[10px] uppercase tracking-[0.22em] text-zinc-500">{title}</div>
      <table className="w-full text-sm">
        <tbody className="divide-y divide-zinc-100">
          {rows.length === 0 && <tr><td className="p-4 text-zinc-500">Aucun mouvement.</td></tr>}
          {rows.map((r) => (
            <tr key={r.code}><td className="px-4 py-2 font-mono text-xs w-24">{r.code}</td><td className="px-4 py-2">{r.name}</td><td className="px-4 py-2 text-right">{fmtN(r.amount)}</td></tr>
          ))}
        </tbody>
        <tfoot className="bg-zinc-50 border-t border-zinc-200 font-medium">
          <tr><td colSpan={2} className="px-4 py-2">Total</td><td className="px-4 py-2 text-right">{fmtN(total)}</td></tr>
        </tfoot>
      </table>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap gap-3 items-end">
          <div><Label>Du</Label><DatePicker value={from} onChange={setFrom} testid="tva-from" /></div>
          <div><Label>Au</Label><DatePicker value={to} onChange={setTo} testid="tva-to" /></div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => downloadXlsx(`/accounting/files/${file.id}/tax/tva?from_d=${from}&to_d=${to}&format=xlsx`, `etat_tva_${file.name}.xlsx`)} data-testid="tva-export">
            <DownloadSimple size={16} className="mr-1" /> Excel
          </Button>
          <Button variant="outline" onClick={() => downloadXlsx(`/accounting/files/${file.id}/tax/tva?from_d=${from}&to_d=${to}&format=dgi`, `tva_dgi_${file.name}.csv`)} data-testid="tva-export-dgi">
            <DownloadSimple size={16} className="mr-1" /> Export DGI (CSV)
          </Button>
          <Button variant="outline" onClick={() => downloadXlsx(`/accounting/files/${file.id}/tax/tva-form?from_d=${from}&to_d=${to}`, `formulaire_tva_dgi_${from}_${to}.pdf`)} data-testid="tva-form-pdf">
            <DownloadSimple size={16} className="mr-1" /> Formulaire TVA (PDF)
          </Button>
        </div>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Section title={`TVA collectée (443x)`} rows={data.collectee} total={data.total_collectee} />
            <Section title={`TVA déductible (445x)`} rows={data.deductible} total={data.total_deductible} />
          </div>
          <div className={`border rounded-md p-6 flex flex-wrap items-center justify-between gap-4 ${data.net >= 0 ? "border-amber-200 bg-amber-50" : "border-emerald-200 bg-emerald-50"}`} data-testid="tva-net">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Crédit antérieur (4449) : {fmtN(data.credit_anterieur)} {file.currency}</div>
              <div className="font-serif-display text-3xl mt-1">{data.sens}</div>
            </div>
            <div className="font-serif-display text-4xl">{fmtN(Math.abs(data.net))} {file.currency}</div>
          </div>
          <p className="text-xs text-zinc-500">État préparatoire à la déclaration de TVA (écritures validées de la période). L'export DGI reprend les champs à reporter dans la télédéclaration e-services. Vérifiez les brouillards avant dépôt.</p>
        </>
      )}

      <IsAcomptes file={file} />

      <DsfPanel file={file} />
    </div>
  );
}

function IsAcomptes({ file }) {
  const [year, setYear] = React.useState(String(new Date().getFullYear()));
  const [formMonth, setFormMonth] = React.useState(todayISO().slice(0, 7));
  const { data } = useSWR(`/accounting/files/${file.id}/tax/is-acomptes?year=${year}`, fetcher);
  return (
    <div className="space-y-4 border-t border-zinc-200 pt-6" data-testid="is-acomptes">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-2">Acomptes d'impôt sur les sociétés</div>
          <div className="w-32"><Label>Année</Label><Input type="number" value={year} onChange={(e) => setYear(e.target.value)} data-testid="is-year" /></div>
        </div>
        <div className="flex flex-wrap gap-2 items-end">
          <div><Label>Mois du formulaire</Label><Input type="month" value={formMonth} onChange={(e) => setFormMonth(e.target.value)} data-testid="is-form-month" /></div>
          <Button variant="outline" onClick={() => downloadXlsx(`/accounting/files/${file.id}/tax/is-form?month=${formMonth}`, `acompte_is_${formMonth}.pdf`)} data-testid="is-form-pdf">
            <DownloadSimple size={16} className="mr-1" /> Formulaire acompte (PDF)
          </Button>
          <Button variant="outline" onClick={() => downloadXlsx(`/accounting/files/${file.id}/tax/is-acomptes?year=${year}&format=xlsx`, `acomptes_is_${year}.xlsx`)} data-testid="is-export">
            <DownloadSimple size={16} className="mr-1" /> Excel
          </Button>
        </div>
      </div>
      {data && (
        <>
          <div className="border border-zinc-200 bg-white rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                  <th className="px-4 py-2">Mois</th><th className="px-4 py-2 text-right">Chiffre d'affaires</th><th className="px-4 py-2 text-right">Acompte IS ({String(data.rate).replace(".", ",")} %)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {data.rows.length === 0 && <tr><td colSpan={3} className="p-4 text-zinc-500">Aucun chiffre d'affaires enregistré sur {data.year}.</td></tr>}
                {data.rows.map((r) => (
                  <tr key={r.month}><td className="px-4 py-2">{r.month}</td><td className="px-4 py-2 text-right">{fmtN(r.ca)}</td><td className="px-4 py-2 text-right">{fmtN(r.acompte)}</td></tr>
                ))}
              </tbody>
              <tfoot className="bg-zinc-50 border-t border-zinc-200 font-medium">
                <tr><td className="px-4 py-2">Total</td><td className="px-4 py-2 text-right">{fmtN(data.total_ca)}</td><td className="px-4 py-2 text-right" data-testid="is-total">{fmtN(data.total_acomptes)}</td></tr>
              </tfoot>
            </table>
          </div>
          <p className="text-xs text-zinc-500">{data.note}</p>
        </>
      )}
    </div>
  );
}
